/**
 * MetaAutopilotPanel — Regeln, Einstellungen & Freigabe-Liste fuer den Meta-Ads-Autopilot.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bot, Loader2, Settings2, ChevronDown, ChevronRight, Check, X, PlayCircle,
  MapPin, Image as ImageIcon, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = (n, cur = "CHF") =>
  n === null || n === undefined ? "—" : `${cur} ${Number(n).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium text-[var(--studio-text-muted,#6b7280)]">{label}</div>
      {children}
    </div>
  );
}

function GeoPicker({ companyId, geo, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    try {
      const r = await axios.get(`${API}/ads/geo-search`, { params: { company_id: companyId, q: val }, withCredentials: true });
      setResults(r.data.results || []);
    } catch { setResults([]); }
  };
  return (
    <div className="space-y-2">
      <Select value={geo?.mode === "radius" ? "radius" : "country"} onValueChange={(m) => onChange(m === "country" ? { mode: "country", country: "CH" } : { mode: "radius", radius_km: 25 })}>
        <SelectTrigger className="h-8 text-xs" data-testid="autopilot-geo-mode"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="country">Landesweit (Schweiz)</SelectItem>
          <SelectItem value="radius">Regional (Stadt + Umkreis)</SelectItem>
        </SelectContent>
      </Select>
      {geo?.mode === "radius" && (
        <div className="space-y-1.5">
          {geo.city_key ? (
            <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="autopilot-geo-selected">
              <MapPin className="w-3 h-3 text-emerald-600" />
              <span className="flex-1">{geo.city_name}</span>
              <button className="text-gray-400 hover:text-red-500" onClick={() => onChange({ ...geo, city_key: null, city_name: "" })}><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <>
              <Input className="h-8 text-xs" placeholder="Stadt suchen (z.B. Zürich)…" value={q} onChange={(e) => search(e.target.value)} data-testid="autopilot-geo-search" />
              {results.length > 0 && (
                <div className="border rounded bg-white max-h-32 overflow-auto">
                  {results.map((r) => (
                    <button key={r.key} className="block w-full text-left px-2 py-1 text-xs hover:bg-gray-50"
                      onClick={() => { onChange({ ...geo, city_key: r.key, city_name: r.name }); setQ(""); setResults([]); }}
                      data-testid={`autopilot-geo-result-${r.key}`}>
                      {r.name} {r.region ? `(${r.region})` : ""} · {r.country_code}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[var(--studio-text-muted)]">Umkreis</span>
            <Input className="h-7 w-20 text-xs" type="number" min={17} max={80} value={geo.radius_km || 25}
              onChange={(e) => onChange({ ...geo, radius_km: Number(e.target.value) })} data-testid="autopilot-geo-radius" />
            <span className="text-[var(--studio-text-muted)]">km (17–80)</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ ap, companyId, onDecided }) {
  const [busy, setBusy] = useState(null);
  const decide = async (action) => {
    setBusy(action);
    try {
      const r = await axios.post(`${API}/ads/approvals/${ap.id}/${action}`, { company_id: companyId }, { withCredentials: true });
      toast.success(r.data.message);
      onDecided();
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
    finally { setBusy(null); }
  };
  return (
    <div className="border border-violet-200 bg-violet-50/50 rounded-lg p-3 space-y-2" data-testid={`approval-card-${ap.id}`}>
      <div className="flex items-start gap-3">
        {ap.picture_url ? (
          <img src={ap.picture_url} alt="" className="w-16 h-16 rounded object-cover border" />
        ) : (
          <div className="w-16 h-16 rounded border bg-gray-100 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-gray-400" /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{ap.campaign_name}</div>
          <div className="text-xs text-[var(--studio-text-muted)] mt-0.5">{ap.reason}</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <Badge variant="outline" className="text-[10px]">{fmtMoney(ap.daily_budget)}/Tag</Badge>
            <Badge variant="outline" className="text-[10px]"><MapPin className="w-2.5 h-2.5 mr-0.5" />{ap.geo_label}</Badge>
            <Badge variant="outline" className="text-[10px]">{ap.age_range} J.</Badge>
            {ap.media_reason && <Badge variant="outline" className="text-[10px]">{ap.media_reason}</Badge>}
          </div>
        </div>
      </div>
      {ap.copy?.primary_text && (
        <div className="text-xs bg-white border rounded p-2">
          <div className="font-medium">{ap.copy.headline}</div>
          <div className="text-gray-600 mt-0.5 whitespace-pre-wrap">{ap.copy.primary_text}</div>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="h-8 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={!!busy}
          onClick={() => decide("approve")} data-testid={`approval-approve-${ap.id}`}>
          {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Freigeben & live schalten
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-red-600" disabled={!!busy}
          onClick={() => decide("reject")} data-testid={`approval-reject-${ap.id}`}>
          {busy === "reject" ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />} Ablehnen & löschen
        </Button>
      </div>
    </div>
  );
}

export default function MetaAutopilotPanel({ companyId, currency = "CHF", autopilotMode = "off", onChanged }) {
  const [settings, setSettings] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/ads/autopilot/settings`, { params: { company_id: companyId }, withCredentials: true }),
        axios.get(`${API}/ads/approvals`, { params: { company_id: companyId }, withCredentials: true }),
      ]);
      setSettings(s.data.settings);
      setApprovals(a.data);
    } catch { /* noop */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ads/autopilot/settings`, { company_id: companyId, ...settings }, { withCredentials: true });
      setSettings(r.data.settings);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/ads/autopilot/run-now`, { company_id: companyId }, { withCredentials: true });
      toast.success(r.data.message, { duration: 8000 });
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Lauf fehlgeschlagen"); }
    finally { setRunning(false); }
  };

  if (!settings) return null;
  const set = (k, v) => setSettings((p) => ({ ...p, [k]: v }));
  const pending = approvals?.pending || [];

  return (
    <Card data-testid="meta-autopilot-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Bot className="w-4 h-4 text-violet-600" /> Meta-Autopilot
          <Badge variant="outline" className="text-[10px]"><Clock className="w-2.5 h-2.5 mr-0.5" /> läuft täglich 06:15</Badge>
          {pending.length > 0 && (
            <Badge className="bg-violet-600 text-white text-[10px]" data-testid="approvals-pending-badge">{pending.length} Freigabe{pending.length > 1 ? "n" : ""} offen</Badge>
          )}
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={runNow}
            disabled={running || autopilotMode === "off"} data-testid="autopilot-run-now-btn"
            title={autopilotMode === "off" ? "Autopilot ist ausgeschaltet" : "Regeln jetzt sofort prüfen"}>
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Jetzt prüfen
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setOpen(!open)} data-testid="autopilot-settings-toggle">
            <Settings2 className="w-3 h-3" /> Regeln {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending.length > 0 && (
          <div className="space-y-2" data-testid="approvals-list">
            {pending.map((ap) => <ApprovalCard key={ap.id} ap={ap} companyId={companyId} onDecided={() => { load(); onChanged && onChanged(); }} />)}
          </div>
        )}
        {pending.length === 0 && !open && (
          <div className="text-xs text-[var(--studio-text-muted)]">
            Keine offenen Freigaben. Der Autopilot prüft täglich deine Kampagnen gegen die Pausier-Regeln
            {settings.create_enabled ? " und erstellt bei Bedarf neue Kampagnen-Entwürfe zur Freigabe." : "."}
          </div>
        )}
        {open && (
          <div className="border rounded-lg p-3 space-y-4 bg-gray-50/50" data-testid="autopilot-settings-form">
            <div>
              <div className="text-xs font-semibold mb-2">⏸ Pausier-Regeln (Auswertung: letzte 7 Tage)</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Field label="Min. Impressionen">
                  <Input className="h-8 text-xs" type="number" value={settings.min_impressions} onChange={(e) => set("min_impressions", Number(e.target.value))} data-testid="rule-min-impressions" />
                </Field>
                <Field label="Min. CTR (%)">
                  <Input className="h-8 text-xs" type="number" step="0.1" value={settings.min_ctr_pct} onChange={(e) => set("min_ctr_pct", Number(e.target.value))} data-testid="rule-min-ctr" />
                </Field>
                <Field label={`Max. CPC (${currency})`}>
                  <Input className="h-8 text-xs" type="number" step="0.5" value={settings.max_cpc} onChange={(e) => set("max_cpc", Number(e.target.value))} data-testid="rule-max-cpc" />
                </Field>
                <Field label={`Max. Kosten/Lead (${currency})`}>
                  <Input className="h-8 text-xs" type="number" step="5" value={settings.max_cpl} onChange={(e) => set("max_cpl", Number(e.target.value))} data-testid="rule-max-cpl" />
                </Field>
                <Field label={`Max. Ausgaben ohne Lead (${currency})`}>
                  <Input className="h-8 text-xs" type="number" step="5" value={settings.max_spend_no_lead} onChange={(e) => set("max_spend_no_lead", Number(e.target.value))} data-testid="rule-max-spend-no-lead" />
                </Field>
              </div>
              <div className="text-[11px] text-[var(--studio-text-muted)] mt-1.5">
                Pausiert wird, wenn: CTR unter Minimum (ab Min. Impressionen) · CPC über Maximum (ab 10 Klicks) · bei Lead-Kampagnen: Kosten/Lead über Maximum oder Ausgaben ohne Lead über Limit.
              </div>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Switch checked={!!settings.create_enabled} onCheckedChange={(v) => set("create_enabled", v)} data-testid="autopilot-create-switch" />
                <div className="text-xs font-semibold">✨ Neue Kampagnen-Entwürfe automatisch erstellen (nur im Vollautomatik-Modus)</div>
              </div>
              <div className="text-[11px] text-[var(--studio-text-muted)] mb-2">
                Wenn der Autopilot etwas pausiert (oder keine Kampagne mehr läuft), erstellt er einen Ersatz-Entwurf mit dem besten Bild aus der Mediathek + KI-Texten. Der Entwurf bleibt PAUSIERT, bis du ihn hier freigibst.
              </div>
              {settings.create_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label={`Tagesbudget neuer Kampagnen (${currency})`}>
                    <Input className="h-8 text-xs" type="number" step="1" min="1" value={settings.daily_budget} onChange={(e) => set("daily_budget", Number(e.target.value))} data-testid="autopilot-budget" />
                  </Field>
                  <Field label="Kampagnenziel">
                    <Select value={settings.objective} onValueChange={(v) => set("objective", v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="autopilot-objective"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OUTCOME_TRAFFIC">Traffic</SelectItem>
                        <SelectItem value="OUTCOME_AWARENESS">Bekanntheit</SelectItem>
                        <SelectItem value="OUTCOME_ENGAGEMENT">Interaktionen</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Landing-URL (leer = Firmen-Website)">
                    <Input className="h-8 text-xs" placeholder="https://…" value={settings.landing_url || ""} onChange={(e) => set("landing_url", e.target.value)} data-testid="autopilot-landing-url" />
                  </Field>
                  <Field label="Gebiet">
                    <GeoPicker companyId={companyId} geo={settings.geo} onChange={(g) => set("geo", g)} />
                  </Field>
                  <Field label="Alter (von – bis)">
                    <div className="flex items-center gap-1.5">
                      <Input className="h-8 w-16 text-xs" type="number" min={18} max={65} value={settings.age_min} onChange={(e) => set("age_min", Number(e.target.value))} data-testid="autopilot-age-min" />
                      <span className="text-xs">–</span>
                      <Input className="h-8 w-16 text-xs" type="number" min={18} max={65} value={settings.age_max} onChange={(e) => set("age_max", Number(e.target.value))} data-testid="autopilot-age-max" />
                    </div>
                  </Field>
                  <Field label="Geschlecht">
                    <Select value={settings.genders} onValueChange={(v) => set("genders", v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="autopilot-genders"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle</SelectItem>
                        <SelectItem value="female">Frauen</SelectItem>
                        <SelectItem value="male">Männer</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              )}
            </div>
            <Button size="sm" className="h-8 text-xs" onClick={save} disabled={saving} data-testid="autopilot-save-settings">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Einstellungen speichern
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
